/**
 * Session + organization data access for the ServiceApp platform pages.
 * All reads go through RLS (members see only their own org).
 */
import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase.js';
import { applyPlatformTenantSeoBundleTemplateToOrg } from '../../utils/marketingTenantSeoBundle.js';
import { getAppMode } from '../appMode.js';
import { resolveTrialEndsAt, trialDaysRemaining } from './trialBilling.js';
import {
  fetchPublicOrgByHost,
  fetchPublicOrgBySlug,
  fetchPublicOrgBySlugFromMembership,
} from './publicOrg.js';
import { isToolsAndBubblesProductionHost } from './tenantHost.js';
import { getActiveWorkspaceOrgId, setActiveWorkspaceOrgId } from './activeWorkspaceOrgId.js';
import { getImpersonationOverride } from '../../utils/userImpersonationOverride.js';

export interface SaasOrgLocale {
  country?: string;
  timezone?: string;
  currency?: string;
  phone_country_code?: string;
  date_format?: string;
  ui_language?: string;
  rtl?: boolean;
}

export interface SaasOrgTaxConfig {
  system?: string;
  label?: string;
  rate?: number;
  tax_id?: string;
}

export interface SaasOrg {
  id: string;
  slug: string;
  name: string;
  legal_name: string | null;
  status: string;
  industry: string | null;
  locale: SaasOrgLocale;
  tax_config: SaasOrgTaxConfig;
  branding: Record<string, unknown>;
  trial_ends_at: string | null;
  onboarding_completed_at: string | null;
  settings?: Record<string, unknown>;
  /** Live customer site host when Super Admin / DNS attach completed. */
  custom_domain?: string | null;
}

type MembershipRow = {
  organization_id: string;
  created_at: string;
  role?: string;
  organizations: (SaasOrg & { is_default?: boolean }) | (SaasOrg & { is_default?: boolean })[];
};

function unwrapOrg(org: MembershipRow['organizations']): (SaasOrg & { is_default?: boolean }) | null {
  if (!org) return null;
  return Array.isArray(org) ? (org[0] ?? null) : org;
}

function membershipRoleRank(role?: string): number {
  if (role === 'super_admin' || role === 'owner') return 0;
  if (role === 'admin') return 1;
  return 2;
}

/**
 * Pick workspace org when the user belongs to several tenants.
 * 1. Highest org role (super_admin / owner first)
 * 2. Default platform org (Tools & Bubbles) when roles tie
 * 3. Most recent membership
 */
export function pickPreferredMembership(rows: MembershipRow[]): MembershipRow | null {
  if (!rows.length) return null;
  const sorted = [...rows].sort((a, b) => {
    const rankDiff = membershipRoleRank(a.role) - membershipRoleRank(b.role);
    if (rankDiff !== 0) return rankDiff;
    const aOrg = unwrapOrg(a.organizations);
    const bOrg = unwrapOrg(b.organizations);
    const aDefault = aOrg?.is_default ?? false;
    const bDefault = bOrg?.is_default ?? false;
    if (aDefault !== bDefault) return aDefault ? -1 : 1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
  return sorted[0] ?? null;
}

async function fetchPersistedWorkspaceOrgId(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId) return null;
  const { data } = await supabase
    .from('user_workspace_context')
    .select('organization_id')
    .eq('user_id', userId)
    .maybeSingle();
  return data?.organization_id ?? null;
}

function membershipRowToOrg(row: MembershipRow): SaasOrg | null {
  const org = unwrapOrg(row.organizations);
  if (!org) return null;
  const { is_default: _ignored, ...saasOrg } = org;
  return saasOrg as SaasOrg;
}

const MEMBERSHIP_CACHE_TTL_MS = 30_000;
let membershipCache: { rows: MembershipRow[]; expiresAt: number } | null = null;
let membershipInFlight: Promise<MembershipRow[]> | null = null;
let saasWorkspaceSync: { key: string; promise: Promise<SaasOrg | null> } | null = null;

/** Drop cached membership + workspace sync (call on auth changes). */
export function clearSaasMembershipCache(): void {
  membershipCache = null;
  membershipInFlight = null;
  saasWorkspaceSync = null;
}

async function fetchMembershipRows(): Promise<MembershipRow[]> {
  if (membershipCache && membershipCache.expiresAt > Date.now()) {
    return membershipCache.rows;
  }
  if (membershipInFlight) return membershipInFlight;

  membershipInFlight = (async () => {
    try {
      const { data, error } = await supabase
        .from('organization_members')
        .select(
          'organization_id, created_at, role, organizations!inner (id, slug, name, legal_name, status, industry, locale, tax_config, branding, trial_ends_at, onboarding_completed_at, is_default, settings, custom_domain)',
        )
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      const rows = error || !data ? [] : (data as MembershipRow[]);
      membershipCache = { rows, expiresAt: Date.now() + MEMBERSHIP_CACHE_TTL_MS };
      return rows;
    } finally {
      membershipInFlight = null;
    }
  })();

  return membershipInFlight;
}

/** React hook: current Supabase session (null when signed out). */
export function useSaasSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      if (mounted) setSession(s);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { session, loading };
}

async function fetchCustomerAccountOrg(orgId: string): Promise<SaasOrg | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('organization_customer_accounts')
    .select('organization_id')
    .eq('profile_id', user.id)
    .eq('organization_id', orgId)
    .maybeSingle();
  if (!data) return null;
  return fetchOrgById(orgId);
}

/** The caller's organization via preferred membership, or null. */
export async function fetchMyOrg(): Promise<SaasOrg | null> {
  const rows = await fetchMembershipRows();

  // toolsandbubbles.com must stay on the default T&B tenant even if SaaS testing
  // left a non-default workspace (e.g. Sprkle) in user_workspace_context.
  if (getAppMode() === 'tb-site') {
    if (!rows.length) return null;
    const defaultRow = rows.find((r) => Boolean(unwrapOrg(r.organizations)?.is_default));
    if (defaultRow) return membershipRowToOrg(defaultRow);
    const tbRow = rows.find((r) => unwrapOrg(r.organizations)?.slug === TB_SITE_ORG_SLUG);
    if (tbRow) return membershipRowToOrg(tbRow);
  }

  const persistedOrgId = await fetchPersistedWorkspaceOrgId();
  if (persistedOrgId) {
    const persistedRow = rows.find((r) => r.organization_id === persistedOrgId);
    if (persistedRow) return membershipRowToOrg(persistedRow);
    const customerOrg = await fetchCustomerAccountOrg(persistedOrgId);
    if (customerOrg) return customerOrg;
  }

  if (!rows.length) return null;

  const pick = pickPreferredMembership(rows);
  if (!pick) return null;
  return membershipRowToOrg(pick);
}

export type OrgMembershipRow = {
  role: string;
  status: string;
};

async function resolveMembershipUserId(userId?: string): Promise<string | null> {
  if (userId) return userId;
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

/** Membership row for a user in the workspace org, including suspended/removed. */
export async function fetchMyOrgMembership(
  orgId?: string,
  userId?: string,
): Promise<OrgMembershipRow | null> {
  const targetOrgId = orgId ?? (await fetchMyOrg())?.id;
  if (!targetOrgId) return null;
  const targetUserId = await resolveMembershipUserId(userId);
  if (!targetUserId) return null;

  const { data, error } = await supabase
    .from('organization_members')
    .select('role, status')
    .eq('organization_id', targetOrgId)
    .eq('user_id', targetUserId)
    .maybeSingle();
  if (error || !data) return null;
  const row = data as { role?: string; status?: string };
  if (!row.role) return null;
  return { role: row.role, status: row.status ?? 'active' };
}

/** Active organization_members.role for a user in the workspace org. */
export async function fetchMyOrgMemberRole(orgId?: string, userId?: string): Promise<string | null> {
  const membership = await fetchMyOrgMembership(orgId, userId);
  if (!membership || membership.status !== 'active') return null;
  return membership.role;
}

/** Persist active workspace org for RLS (user_workspace_context). */
export async function setWorkspaceOrg(orgId: string): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  // set_workspace_org is granted to authenticated only. Public pages on
  // toolsandbubbles.com (signed-out visitors and crawlers) must not call it —
  // each denial is logged as a Postgres error. The in-memory pin still lets
  // later authed requests send X-Workspace-Org-Id.
  if (!session?.user) {
    setActiveWorkspaceOrgId(orgId);
    return;
  }
  const { error } = await supabase.rpc('set_workspace_org', { p_org_id: orgId });
  if (error) throw error;
  // Keep in-memory id in sync so authedFetch sends X-Workspace-Org-Id on T&B and SaaS.
  setActiveWorkspaceOrgId(orgId);
}

/**
 * toolsandbubbles.com uses client-side Supabase RLS. Stale workspace context from
 * SaaS testing (e.g. test-company) hides the T&B service catalogue on enquiries.
 */
const TB_SITE_ORG_SLUG = 'toolsandbubbles';

const TB_SITE_ORG_SELECT =
  'id, slug, name, legal_name, status, industry, locale, tax_config, branding, trial_ends_at, onboarding_completed_at, settings, custom_domain';

// The T&B tenant row and workspace sync are requested by many components on every
// dashboard render. Without de-duplication that produced hundreds of identical
// `organizations` requests on sign-in (each 401 while the access token was being
// refreshed). Cache the row + dedupe in-flight reads to collapse the storm.
const TB_ORG_CACHE_TTL_MS = 60_000;
const TB_ORG_MISS_TTL_MS = 5_000; // short, so we recover quickly once the token refreshes
let tbOrgCache: { org: SaasOrg | null; expiresAt: number } | null = null;
let tbOrgInFlight: Promise<SaasOrg | null> | null = null;
const TB_WORKSPACE_SYNC_OK_TTL_MS = 3_000;
let tbWorkspaceSync: { key: string; promise: Promise<void>; expiresAt?: number } | null = null;

/** Drop cached T&B tenant/workspace state (call on auth changes). */
export function clearTbSiteOrgCache(): void {
  tbOrgCache = null;
  tbOrgInFlight = null;
  tbWorkspaceSync = null;
}

// Re-resolve tenant context whenever the auth state flips so a new sign-in does
// not reuse the previous user's cached workspace sync.
supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
    clearTbSiteOrgCache();
    clearSaasMembershipCache();
  }
});

/** Direct Supabase read when /api/saas/public-org is unavailable on T&B production. */
async function fetchTbSiteOrganizationDirect(): Promise<SaasOrg | null> {
  const { data, error } = await supabase
    .from('organizations')
    .select(TB_SITE_ORG_SELECT)
    .eq('slug', TB_SITE_ORG_SLUG)
    .maybeSingle();
  if (error) {
    console.warn('fetchTbSiteOrganizationDirect:', error.message);
    return null;
  }
  return (data as SaasOrg | null) ?? null;
}

/** toolsandbubbles.com tenant row (single-org site). Cached + de-duplicated. */
export async function fetchTbSiteOrganization(): Promise<SaasOrg | null> {
  if (getAppMode() !== 'tb-site') return null;

  if (tbOrgCache && tbOrgCache.expiresAt > Date.now()) return tbOrgCache.org;
  if (tbOrgInFlight) return tbOrgInFlight;

  tbOrgInFlight = (async () => {
    try {
      // Direct Supabase first. The public-org/membership path used to call
      // company-settings helpers that re-enter this function and never return.
      let org = await fetchTbSiteOrganizationDirect();
      // Customers are not org members, so the direct table read is blocked by RLS.
      // Resolve via the anon public-org RPC: host first (T&B production), then slug.
      if (
        !org &&
        typeof window !== 'undefined' &&
        isToolsAndBubblesProductionHost(window.location.hostname)
      ) {
        try {
          const payload = await fetchPublicOrgByHost(window.location.hostname);
          org = (payload?.organization as SaasOrg | undefined) ?? null;
        } catch (err) {
          console.warn(
            'fetchTbSiteOrganization: public-org host lookup failed',
            err instanceof Error ? err.message : err,
          );
        }
      }
      if (!org) {
        try {
          const payload = await fetchPublicOrgBySlug(TB_SITE_ORG_SLUG);
          org = (payload?.organization as SaasOrg | undefined) ?? null;
        } catch (err) {
          console.warn(
            'fetchTbSiteOrganization: public-org API failed',
            err instanceof Error ? err.message : err,
          );
        }
      }

      if (!org) {
        console.warn('fetchTbSiteOrganization: T&B org not found');
        tbOrgCache = { org: null, expiresAt: Date.now() + TB_ORG_MISS_TTL_MS };
        return null;
      }

      tbOrgCache = { org, expiresAt: Date.now() + TB_ORG_CACHE_TTL_MS };
      return org;
    } catch (err) {
      console.warn('fetchTbSiteOrganization:', err instanceof Error ? err.message : 'lookup failed');
      tbOrgCache = { org: null, expiresAt: Date.now() + TB_ORG_MISS_TTL_MS };
      return null;
    } finally {
      tbOrgInFlight = null;
    }
  })();

  return tbOrgInFlight;
}

const WORKSPACE_SYNC_TIMEOUT_MS = 8_000;

function settleWorkspaceSync(promise: Promise<void>): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      console.warn('ensureTbSiteWorkspaceOrg: timed out; continuing without blocking dashboards');
      if (tbWorkspaceSync?.promise === promise) tbWorkspaceSync = null;
      resolve();
    }, WORKSPACE_SYNC_TIMEOUT_MS);
    promise.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      (err) => {
        clearTimeout(timer);
        console.warn('ensureTbSiteWorkspaceOrg:', err);
        resolve();
      },
    );
  });
}

export async function ensureTbSiteWorkspaceOrg(): Promise<void> {
  if (getAppMode() !== 'tb-site') return;

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) return;

  // De-dupe in-flight calls. Do not cache success forever: a SaaS tab shares
  // user_workspace_context and can pin another tenant (e.g. Fatiha), which
  // would then steal T&B enquiry inserts via request_org_id().
  if (tbWorkspaceSync && tbWorkspaceSync.key === session.user.id) {
    const inFlight = tbWorkspaceSync.expiresAt == null;
    const stillFresh = Boolean(tbWorkspaceSync.expiresAt && tbWorkspaceSync.expiresAt > Date.now());
    if (inFlight || stillFresh) {
      return settleWorkspaceSync(tbWorkspaceSync.promise);
    }
  }

  let failed = false;
  const promise = (async () => {
    const org = await fetchTbSiteOrganization();
    if (!org?.id) {
      console.warn('ensureTbSiteWorkspaceOrg: T&B org not found');
      failed = true;
      return;
    }
    try {
      setActiveWorkspaceOrgId(org.id);
      await setWorkspaceOrg(org.id);
      const { data: resolvedOrgId, error: resolveError } = await supabase.rpc('request_org_id');
      if (resolveError || !resolvedOrgId || resolvedOrgId !== org.id) {
        console.warn('ensureTbSiteWorkspaceOrg: request_org_id still mismatched after set_workspace_org', {
          expected: org.id,
          resolved: resolvedOrgId,
          resolveError: resolveError?.message,
        });
        failed = true;
      }
    } catch (err) {
      console.warn('ensureTbSiteWorkspaceOrg:', err);
      failed = true;
    }
  })();

  tbWorkspaceSync = { key: session.user.id, promise };
  await settleWorkspaceSync(promise);
  if (tbWorkspaceSync?.promise !== promise) return;
  if (failed) {
    tbWorkspaceSync = null; // allow a later retry after token refresh
  } else {
    tbWorkspaceSync = {
      key: session.user.id,
      promise,
      expiresAt: Date.now() + TB_WORKSPACE_SYNC_OK_TTL_MS,
    };
  }
}

/** Tenant row by id — used when the workspace is already pinned (impersonation / switch). */
export async function fetchOrgById(orgId: string): Promise<SaasOrg | null> {
  const id = orgId.trim();
  if (!id) return null;
  const { data, error } = await supabase
    .from('organizations')
    .select(TB_SITE_ORG_SELECT)
    .eq('id', id)
    .maybeSingle();
  if (error || !data) return null;
  return data as SaasOrg;
}

/**
 * Prefer the in-memory workspace pin over preferred/default membership.
 * Platform owners otherwise snap back to Tools & Bubbles and leak that org's settings.
 */
export function resolveEnsureWorkspaceTargetId(
  pinnedOrgId: string | null | undefined,
  preferredOrgId: string | null | undefined,
): string | null {
  return pinnedOrgId?.trim() || preferredOrgId?.trim() || null;
}

/** Resolve preferred org and sync DB workspace context for tenant-scoped RLS. */
export async function ensureWorkspaceOrg(): Promise<SaasOrg | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;
  if (!userId) return fetchMyOrg();

  const impersonationOrgId = getImpersonationOverride(userId)?.workspaceOrgId?.trim() || null;
  if (impersonationOrgId) {
    setActiveWorkspaceOrgId(impersonationOrgId);
  }

  const pinned = getActiveWorkspaceOrgId();
  const cacheKey = `${userId}:${pinned ?? 'preferred'}`;
  if (saasWorkspaceSync && saasWorkspaceSync.key === cacheKey) {
    return saasWorkspaceSync.promise;
  }

  const promise = (async () => {
    const pin = getActiveWorkspaceOrgId();
    if (pin) {
      try {
        await setWorkspaceOrg(pin);
      } catch (err) {
        console.warn('ensureWorkspaceOrg: pinned workspace', err);
      }
      return (await fetchOrgById(pin)) ?? (await fetchWorkspaceContextOrg());
    }

    const org = await fetchMyOrg();
    if (!org) return null;

    // Impersonation / org switch may pin Shwe while preferred membership is still T&B.
    const pinAfter = getActiveWorkspaceOrgId();
    const targetId = resolveEnsureWorkspaceTargetId(pinAfter, org.id);
    if (targetId) {
      try {
        await setWorkspaceOrg(targetId);
      } catch (err) {
        console.warn('ensureWorkspaceOrg:', err);
      }
    }
    if (pinAfter && pinAfter !== org.id) {
      return (await fetchOrgById(pinAfter)) ?? (await fetchWorkspaceContextOrg());
    }
    return org;
  })();

  saasWorkspaceSync = { key: cacheKey, promise };
  return promise;
}

/**
 * Resolve the org for the *currently persisted* workspace context (server-side
 * user_workspace_context via request_org_id). Used while a platform admin is
 * impersonating a tenant — the visible workspace must follow the tenant they
 * opened, not their own membership org.
 */
export async function fetchWorkspaceContextOrg(): Promise<SaasOrg | null> {
  const { data: orgId, error } = await supabase.rpc('request_org_id');
  if (error || !orgId) return null;
  const { data } = await supabase
    .from('organizations')
    .select(TB_SITE_ORG_SELECT)
    .eq('id', orgId)
    .maybeSingle();
  return (data as SaasOrg | null) ?? null;
}

export async function initPublicBookingWorkspace(
  orgSlug: string,
): Promise<{ organization_id: string; mode: string } | { error: string }> {
  const slug = orgSlug.trim().toLowerCase();
  if (!slug) return { error: 'slug_required' };

  const { data, error } = await supabase.rpc('init_public_booking_workspace', { p_slug: slug });
  if (error) return { error: error.message };
  return data as { organization_id: string; mode: string };
}

/** Guest embed flow: anonymous session + tenant workspace for RLS-scoped booking reads. */
export async function ensurePublicBookingSession(
  orgSlug: string,
  options?: { organizationId?: string },
): Promise<{ error?: string }> {
  const slug = orgSlug.trim().toLowerCase();
  if (!slug && !options?.organizationId) return { error: 'slug_required' };

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    const { error: anonError } = await supabase.auth.signInAnonymously();
    if (anonError) return { error: anonError.message };
  }

  if (options?.organizationId) {
    try {
      await setWorkspaceOrg(options.organizationId);
      return {};
    } catch {
      /* try slug-based init below */
    }
  }

  const result = await initPublicBookingWorkspace(slug);
  if ('error' in result) {
    const membershipOrg = await fetchPublicOrgBySlugFromMembership(slug);
    if (membershipOrg?.organization.id) {
      try {
        await setWorkspaceOrg(membershipOrg.organization.id);
        return {};
      } catch (err) {
        return { error: err instanceof Error ? err.message : result.error };
      }
    }
    return { error: result.error };
  }
  return {};
}

export async function fetchMySubscription(orgId: string): Promise<SaasSubscription | null> {
  const { data } = await supabase
    .from('organization_subscriptions')
    .select('plan_code, status, trial_end, current_period_end, stripe_customer_id, stripe_subscription_id')
    .eq('organization_id', orgId)
    .in('status', ['trialing', 'active', 'past_due', 'paused'])
    .maybeSingle();
  return (data as SaasSubscription | null) ?? null;
}

/** Ensures the signed-in user has a super_admin profile (org owner on ServiceApp). */
export async function ensureSaasOwnerProfile(): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user?.id) return;
  const { error } = await supabase.rpc('saas_upsert_org_owner_profile', {
    p_user_id: session.user.id,
  });
  if (error) {
    console.warn('ensureSaasOwnerProfile:', error.message);
  }
}

export async function createOrganization(params: {
  companyName: string;
  planCode?: string;
  industry?: string;
  country?: string;
}): Promise<{ organization_id: string; created: boolean } | { error: string }> {
  const { data, error } = await supabase.rpc('signup_create_organization', {
    p_company_name: params.companyName,
    p_plan_code: params.planCode ?? 'grow',
    p_industry: params.industry ?? null,
    p_country: params.country ?? null,
  });
  if (error) return { error: error.message };
  const result = data as { organization_id: string; created: boolean };
  if (result.organization_id) {
    try {
      await setWorkspaceOrg(result.organization_id);
    } catch (err) {
      console.warn('createOrganization setWorkspaceOrg:', err);
    }
    if (result.created) {
      applyPlatformTenantSeoBundleTemplateToOrg(result.organization_id).catch((err) => {
        console.warn('createOrganization applyPlatformTenantSeoBundleTemplateToOrg:', err);
      });
    }
    void supabase
      .rpc('saas_link_sales_leads_to_new_org', { p_org: result.organization_id })
      .then(({ error }) => {
        if (error) console.warn('createOrganization link sales leads:', error.message);
      });
  }
  return result;
}

export async function updateMyOrg(
  orgId: string,
  patch: Partial<{
    name: string;
    legal_name: string | null;
    industry: string | null;
    locale: SaasOrgLocale;
    tax_config: SaasOrgTaxConfig;
    branding: Record<string, unknown>;
    onboarding_completed_at: string;
  }>,
): Promise<string | null> {
  const { error } = await supabase.from('organizations').update(patch).eq('id', orgId);
  return error ? error.message : null;
}

export async function fetchOrgUsageSummary(orgId?: string): Promise<{
  plan_code?: string;
  limits?: {
    bookings_per_month?: number;
    enquiries_per_month?: number;
    ai_messages_per_month?: number;
    emails_per_month?: number;
  };
  usage?: {
    bookings?: number;
    enquiries?: number;
    visits?: number;
    ai_messages?: number;
    emails?: number;
  };
  ai_credit_balance?: number;
} | null> {
  const { data, error } = await supabase.rpc('get_org_usage_summary', {
    p_org: orgId ?? null,
  });
  if (error) {
    console.warn('fetchOrgUsageSummary:', error.message);
    return null;
  }
  return (data as Record<string, unknown>) ?? null;
}

export function trialDaysLeft(
  org: SaasOrg,
  sub?: Pick<SaasSubscription, 'status' | 'trial_end'> | null,
): number | null {
  return trialDaysRemaining(resolveTrialEndsAt(org, sub ?? null));
}

export interface SaasSubscription {
  plan_code: string;
  status: string;
  trial_end: string | null;
  current_period_end: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
}
